import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  IconButton,
  Stack,
  Typography,
  Tooltip,
  Box,
  Fade,
  IconButtonProps,
  useTheme,
  useMediaQuery,
  BoxProps,
  Avatar,
} from '@mui/material';
import { Document, Page } from 'react-pdf';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import FlagIcon from '@mui/icons-material/Flag';
import ContrastIcon from '@mui/icons-material/Contrast';

import ReportModal from '../../pages/UserPages/ManageSubmissionQuestions/modals/ReportModal';
import AddQuestionsModal from '../../pages/UserPages/Resources/modals/AddQuestionsModal';
import QuestionPlacementModal from '../../pages/UserPages/Resources/modals/QuestionPlacementModal';
import MenuButton from './MenuButton';
import PageSlider from './PageSlider';
import TutorAssess from '../../pages/UserPages/Resources/components/TutorAssess';

import { TP_COLORS } from '../../constants';
import { scales } from '../../pages/UserPages/Resources';
import { useAppSelector } from '../../hooks';
import { getAuthState } from '../../store/AuthSlice';
import { reportResources } from '../../store/ResourcesSlice';
import { getSubTopicState } from '../../store/SubTopicSlice';
import { getSessionState, resetQuestionList } from '../../store/SessionSlice';
import socket from '../../helper/Socket';

const APP_BAR_BG_COLOR = '#131416';

interface PdfViewerHeaderProps {
  fileUrl: string;
  maxWidth?: string;
  onChangePage: (pageNo: string) => void;
  totalPages: number;
  pageNo: string;
  onScaleChange: (type: 'inc' | 'dec') => void;
  scale: number;
  appBarProps?: BoxProps['sx'];
  onFullScreenToggle: () => void;
  toggleTheme: () => void;
  isInFullScreen?: boolean;
  setIsWhiteboardEnable: React.Dispatch<React.SetStateAction<boolean>>;
}

const defaultPdfMenuItemStyles: React.CSSProperties = {
  width: '90px',
  marginRight: '10px',
  borderBottom: '1px solid transparent',
  cursor: 'pointer',
  display: 'inline-block',
};

const iconButtonStyles: IconButtonProps['style'] = {
  backgroundColor: TP_COLORS.black,
  borderRadius: '7px',
  marginRight: '5px',
};

const CustomErrorComponent = (
  <Typography
    variant="h5"
    fontWeight={600}
    textAlign="center"
    sx={{ m: '30px 0' }}
  >
    Failed to load PDF file
  </Typography>
);

const PdfViewerHeader: React.FC<PdfViewerHeaderProps> = ({
  fileUrl,
  maxWidth,
  totalPages,
  pageNo,
  scale,
  onChangePage,
  onScaleChange,
  appBarProps = {},
  onFullScreenToggle,
  toggleTheme,
  isInFullScreen = false,
  setIsWhiteboardEnable,
}) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const belowSm = useMediaQuery(theme.breakpoints.down('sm'));

  // Store
  const authState = useAppSelector(getAuthState);
  const subTopicState = useAppSelector(getSubTopicState);
  const sessionState = useAppSelector(getSessionState);

  const authUser = authState.user;
  const subTopicData = subTopicState.subTopic;
  const { session } = sessionState;
  const questions = session?.questions;
  const userRole = authUser?.role.name;

  // State
  const [pages, setPages] = useState<JSX.Element[]>([]);
  const [isMenuOpen, setMenuOpen] = useState(false);
  const [isReportModalOpen, setReportModalOpen] = useState(false);
  const [isQuestionsModalOpen, setQuestionsModalOpen] = useState(false);
  const [isQuestionPlaceModalOpen, setQuestionPlaceModalOpen] = useState(false);
  const [checkedQuestions, setCheckedQuestions] = useState<string[]>([]);

  const findQuestionPage = (page: string) =>
    questions.find((question) => Number(question.pageNo) === Number(page));

  const isZoomInDisabled =
    scales[scales.length - 1] === scale ||
    pageNo === (totalPages + 1).toString() ||
    !!findQuestionPage(pageNo);

  const isZoomOutDisabled =
    scales[0] === scale ||
    pageNo === (totalPages + 1).toString() ||
    !!findQuestionPage(pageNo);

  useEffect(() => {
    const menuPages: JSX.Element[] = [];

    if (totalPages) {
      for (let i = 1; i <= totalPages; i += 1) {
        const pdfMenuItemStyles: React.CSSProperties = {
          ...defaultPdfMenuItemStyles,
        };

        if (Number(pageNo) === i) {
          pdfMenuItemStyles.borderBottom = `1px solid ${TP_COLORS.white}`;
        }

        if (i === totalPages) {
          pdfMenuItemStyles.marginRight = '0';
        }

        const questionPage = findQuestionPage(i.toString());

        if (questionPage) {
          menuPages.push(
            <button
              type="button"
              className="btn-no-style hover-effect-opacity-dot-5"
              id={`pdf-menu-item-${i}`}
              key={`pdf-menu-item-${i}`}
              style={pdfMenuItemStyles}
              onClick={() => {
                onChangePage(i.toString());
                setMenuOpen(false);
              }}
            >
              <div
                style={{
                  width: '100px',
                  height: '70px',
                  maxHeight: '70px',
                  overflow: 'hidden',
                  borderRadius: '7px',
                  background: TP_COLORS.white,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography
                  color="primary.main"
                  fontSize="12px"
                  fontWeight={400}
                >
                  Question
                </Typography>
              </div>
              <Typography
                color={TP_COLORS.white}
                textAlign="center"
                style={{ marginTop: '5px' }}
              >
                {i.toString()}
              </Typography>
            </button>
          );
        } else {
          menuPages.push(
            <button
              type="button"
              className="btn-no-style hover-effect-opacity-dot-5"
              id={`pdf-menu-item-${i}`}
              key={`pdf-menu-item-${i}`}
              style={pdfMenuItemStyles}
              onClick={() => {
                onChangePage(i.toString());
                setMenuOpen(false);
              }}
            >
              <div
                style={{
                  minHeight: '70px',
                  maxHeight: '70px',
                  overflow: 'hidden',
                  borderRadius: '7px',
                  background: TP_COLORS.white,
                }}
              >
                <Page
                  pageNumber={
                    i - questions.filter((ele) => Number(ele.pageNo) < i).length
                  }
                  className="pdf-rounded"
                  width={100}
                />
              </div>
              <Typography
                color={TP_COLORS.white}
                textAlign="center"
                style={{ marginTop: '5px' }}
              >
                {i.toString()}
              </Typography>
            </button>
          );
        }
      }
    }

    setPages(menuPages);
  }, [totalPages, pageNo, questions]);

  const handleReportQuestion = (reason: string) => {
    const questionPage = findQuestionPage(pageNo);
    if (subTopicData?._id && authUser?._id) {
      dispatch(
        reportResources({
          ...(questionPage && questionPage.question
            ? { questionId: questionPage.question._id }
            : { pageNo }),
          reason,
          subTopicId: subTopicData._id,
          userId: authUser._id,
        })
      );
    }
    setReportModalOpen(false);
  };

  const handleAddQuestion = (questionsList: string[]) => {
    setCheckedQuestions(questionsList);
    setQuestionsModalOpen(false);
    setQuestionPlaceModalOpen(true);
  };

  const handleQuestionSubmit = (value: string) => {
    const questionPage = findQuestionPage(pageNo);
    const questionsLength = questions.filter(
      (ele) => Number(ele.pageNo) < Number(pageNo)
    ).length;
    const newCheckedQuestions = checkedQuestions.map((question) => ({
      question,
      pageNo:
        value === 'before'
          ? Number(pageNo) - questionsLength
          : Number(pageNo) - questionsLength + (questionPage ? 0 : 1),
    }));
    socket.emit('send_question', newCheckedQuestions[0]);
    setQuestionPlaceModalOpen(false);
  };

  const checkTutorAssessEnabled = () => {
    if (
      subTopicData &&
      subTopicData.topic &&
      subTopicData.name.toLocaleLowerCase().search('worksheet') < 0 &&
      subTopicData.name.toLocaleLowerCase().search('overview') < 0 &&
      subTopicData.name.toLocaleLowerCase().search('topic planner') < 0 &&
      subTopicData.topic.name.toLocaleLowerCase().search('overview') < 0
    ) {
      return true;
    }
    return false;
  };

  return (
    <>
      {isMenuOpen ? (
        <Box
          id="pdf-drop-down-container"
          onMouseLeave={() => setMenuOpen(false)}
          sx={{
            maxWidth,
            zIndex: 10,
            transition: '0.5s all ease',
            position: 'absolute',
            width: maxWidth || '100%',
            color: TP_COLORS.white,
            background: APP_BAR_BG_COLOR,
            mt: '-1px',
            top: {
              xs: isInFullScreen ? 'calc(56px)' : 'calc(184px + 56px)',
              sm: '565px',
            },
          }}
        >
          <Document
            renderMode="canvas"
            file={{ url: fileUrl }}
            loading=""
            error={CustomErrorComponent}
            onLoadSuccess={() => {
              if (Number(pageNo) > 5) {
                setTimeout(() => {
                  window.document
                    .getElementById(`pdf-menu-item-${pageNo}`)
                    ?.scrollIntoView({
                      inline: 'center',
                      block: 'nearest',
                    });
                }, 100);
              }
            }}
          >
            <Fade in timeout={1000}>
              <Stack
                direction="row"
                alignItems="center"
                style={{
                  overflowX: 'auto',
                  overflowY: 'hidden',
                  padding: '10px',
                }}
              >
                {pages}
              </Stack>
            </Fade>
          </Document>
        </Box>
      ) : null}

      <Box
        sx={{
          ...appBarProps,
          width: '100%',
          maxWidth,
          borderRadius: '7px',
          // height: '66px',
          backgroundColor: TP_COLORS.white,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '10px',
          boxShadow: '0px 14px 40px 0px rgba(110, 157, 178, 0.18)',
        }}
      >
        <Box
          sx={{
            display: { xs: 'flex', sm: 'none' },
            width: '100%',
            marginTop: '5px',
            marginBottom: '15px',
          }}
        >
          <PageSlider
            pageNo={Number(pageNo)}
            totalPages={totalPages}
            onChangePage={(page) => {
              // setMenuOpen(true);
              onChangePage(page);
            }}
          />
        </Box>

        <Stack flexDirection="row" justifyContent="space-between" width="100%">
          <Box
            display="flex"
            alignItems="center"
            sx={{
              width: {
                xs: 'calc(100% - 40px)',
                sm: `calc(100% - 90px - ${
                  userRole === 'Tutor' ? '85px' : '0px'
                })`,
                md: '60%',
              },
            }}
          >
            <Box sx={{ display: { xs: 'none', sm: 'flex' }, width: '100%' }}>
              <PageSlider
                pageNo={Number(pageNo)}
                totalPages={totalPages}
                onChangePage={(page) => {
                  // setMenuOpen(true);
                  onChangePage(page);
                }}
              />
            </Box>

            <Tooltip title="Zoom Out">
              <span>
                <IconButton
                  disabled={isZoomOutDisabled}
                  key="action-btn-group-item-1"
                  onClick={() => onScaleChange('dec')}
                  style={{
                    ...iconButtonStyles,
                    opacity: isZoomOutDisabled ? 0.7 : 1,
                  }}
                >
                  <RemoveIcon style={{ color: TP_COLORS.white }} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Zoom In">
              <span>
                <IconButton
                  disabled={isZoomInDisabled}
                  key="action-btn-group-item-3"
                  onClick={() => onScaleChange('inc')}
                  style={{
                    ...iconButtonStyles,
                    opacity: isZoomInDisabled ? 0.7 : 1,
                  }}
                >
                  <AddIcon style={{ color: TP_COLORS.white }} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip
              title={isInFullScreen ? 'Exit Full Screen' : 'Enter Full Screen'}
            >
              <IconButton onClick={onFullScreenToggle} style={iconButtonStyles}>
                {isInFullScreen ? (
                  <CloseFullscreenIcon style={{ color: TP_COLORS.white }} />
                ) : (
                  <FullscreenIcon style={{ color: TP_COLORS.white }} />
                )}
              </IconButton>
            </Tooltip>
          </Box>

          <Box display="flex" alignItems="center">
            {userRole === 'Tutor' ? (
              <>
                {checkTutorAssessEnabled() && <TutorAssess pageNo={pageNo} />}
                {!belowSm && (
                  <Tooltip title="Whiteboard">
                    <IconButton
                      onClick={() =>
                        setIsWhiteboardEnable((prevState) => !prevState)
                      }
                      style={iconButtonStyles}
                    >
                      <Avatar
                        sx={{ width: '24px', height: '24px' }}
                        src={`${process.env.PUBLIC_URL}/images/subtopicPage/whiteboard-icon.png`}
                      />
                    </IconButton>
                  </Tooltip>
                )}
                <MenuButton
                  toggleTheme={toggleTheme}
                  handleQuestionsModalOpen={() => setQuestionsModalOpen(true)}
                  handleReportModalOpen={() => setReportModalOpen(true)}
                  handleWhiteboardEnable={() =>
                    setIsWhiteboardEnable((prevState) => !prevState)
                  }
                />
              </>
            ) : (
              <>
                <Tooltip title="Change theme">
                  <IconButton onClick={toggleTheme} style={iconButtonStyles}>
                    <ContrastIcon style={{ color: TP_COLORS.white }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Report this resource">
                  <IconButton
                    onClick={() => setReportModalOpen(true)}
                    style={iconButtonStyles}
                  >
                    <FlagIcon style={{ color: TP_COLORS.white }} />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Box>
        </Stack>
      </Box>

      {/* Modals */}
      {isReportModalOpen && (
        <ReportModal
          open={isReportModalOpen}
          type="resource"
          onClose={() => setReportModalOpen(false)}
          onConfirm={handleReportQuestion}
        />
      )}
      {isQuestionsModalOpen && (
        <AddQuestionsModal
          open={isQuestionsModalOpen}
          onClose={() => {
            setQuestionsModalOpen(false);
            dispatch(resetQuestionList());
          }}
          onConfirm={handleAddQuestion}
        />
      )}
      {isQuestionPlaceModalOpen && (
        <QuestionPlacementModal
          open={isQuestionPlaceModalOpen}
          onClose={() => setQuestionPlaceModalOpen(false)}
          onConfirm={handleQuestionSubmit}
        />
      )}
    </>
  );
};

export default PdfViewerHeader;
